import * as React from 'react';

import { Dispatch } from 'redux';
import AppState from '@webapp/store/state';
import { Category } from '@common/types/categories/category';
import { UpdateField } from '@webapp/store/release/action-creators';
import { connect } from 'react-redux';

import { formatStores } from '../../../utils/normalizers';
import { ReleaseStore } from '@common/types/release/release-store';
import { GenericDropdownBuilder } from '../../form/dropdown/index';
import Radio from '@components/form/radio';
import styled from 'styled-components';
import { hasPlanFeature } from '@webapp/sdk/plans';
import { PlanNames } from '@services/product/types/plan-names';
import { Translate } from 'react-localize-redux';

interface StoreSelectorCtrlProps {
  className?: string;
  stores: Category<ReleaseStore>[];
  active: ReleaseStore[];
  brief?: string;
  planHasAppleMusic: boolean;
  updateStores: (stores: ReleaseStore[]) => UpdateField<'stores'>;
}
const mapStateToProps = (state: AppState) => ({
  stores: state.categories.lists['platform'] || [],
  active: state.release.release?.stores || [],
  brief: state.release.release?.storesBrief,
  planHasAppleMusic: hasPlanFeature(
    state.subscription.current?.plan.toLocaleLowerCase() as PlanNames,
    'RELEASE.APPLE_DISTRIBUTION'
  ),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateStores: (stores: ReleaseStore[]) =>
    dispatch(new UpdateField<'stores'>('stores', stores)),
});

const DropdownCtrl = GenericDropdownBuilder<ReleaseStore[]>();

export const StoreSelectorCtrl: React.FC<StoreSelectorCtrlProps> = (props) => {
  const { stores, active } = props;
  const [filteredStore, setFilteredStore] =
    React.useState<Category<ReleaseStore>[]>(stores);
  const [filteredActive, setFilteredActive] =
    React.useState<ReleaseStore[]>(active);

  React.useEffect(() => {
    let filtered = stores;
    let filteredActive = active;
    setFilteredActive(filteredActive);
    setFilteredStore(filtered);
  }, [stores, active]);

  const getTitle = () => {
    return (
      props.brief ||
      formatStores(
        active,
        stores.map((s) => s.name)
      )
    );
  };

  const isSelected = (storeId: string) => {
    const allSelected = filteredActive.length === filteredStore.length;

    if (storeId === 'all') {
      return allSelected;
    }

    if (filteredActive.find((a) => storeId === a.id)) {
      return true;
    }

    return false;
  };

  const handleSelection = (store?: ReleaseStore) => {
    let newStores: ReleaseStore[] = props.active || [];

    if (!store) {
      if (isSelected('all')) {
        newStores = [];
      } else {
        newStores = props.stores
          .filter((c) => !!c.metadata)
          .map((c) => c.metadata as ReleaseStore);
      }
    }

    if (store) {
      if (isSelected(store.id)) {
        newStores = newStores.filter((c) => c.id !== store.id);
      } else {
        newStores = newStores.concat([store]);
      }
    }
    props.updateStores(newStores);
  };

  return (
    <div className={props.className}>
      <DropdownCtrl
        id="storesDropdown"
        title={<Translate id="RELEASE.DATES_STORES.STORES.LABEL" />}
        value={props.active}
        valueMapper={() => getTitle()}
      >
        <div>
          <Radio
            className="radio-store"
            isChecked={isSelected('all')}
            onChange={() => handleSelection()}
          >
            <Translate
              id="RELEASE.DATES_STORES.STORES.OPTION.ALL"
              data={{ amount: filteredStore.length }}
            />
          </Radio>
          {props.stores.map((store) => (
            <Radio
              key={`store-${store.pk}`}
              className="radio-store"
              isChecked={isSelected(store.metadata?.id as string)}
              onChange={() => handleSelection(store.metadata as ReleaseStore)}
            >
              {store.name}
            </Radio>
          ))}
        </div>
      </DropdownCtrl>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(StoreSelectorCtrl)`
  .radio-store {
    margin-top: 14px;

    &:first-child {
      margin-top: 0;
    }
  }
`);
